import { Factory, trait, association } from 'miragejs';
import faker from 'faker';
import { addDays } from 'date-fns';

export const randomHtmlText = (paragraphsCount = 3) =>
  faker.lorem
    .paragraphs(paragraphsCount)
    .split('\n \r')
    .filter(Boolean)
    .reduce((acc, paragraph) => `${acc}<p>${paragraph}</p>`, '');

export const withSeo = ({ seoDescriptionSentences, slugWordCount } = {}) => ({
  seoDescription: () => faker.lorem.sentences(seoDescriptionSentences),
  slug: () => faker.lorem.slug(slugWordCount),
});

export const withVersionDate = ({
  dateCreated = faker.date.past(),
  dateModified = addDays(dateCreated, 1),
  datePublished = addDays(dateCreated, 2),
  languages = [],
} = {}) => {
  const created = {
    created: () => dateCreated.toISOString(),
    createdBy: association(),
  };
  const modified = {
    modified: () => addDays(dateModified, 1).toISOString(),
    modifiedBy: association(),
  };
  const published = {
    published: () => addDays(datePublished, 2).toISOString(),
    publishedBy: association(),
  };

  const isModified = trait({ ...modified });
  const isPublished = trait({ ...modified, ...published });

  return {
    ...created,
    modified: null,
    published: null,
    isModified,
    isPublished,
    ...languages.reduce(
      (acc, language) => ({
        ...acc,
        [`isModified:${language}`]: isModified,
        [`isPublished:${language}`]: isPublished,
      }),
      {},
    ),
  };
};

export const withVersionedContent = ({ draftContent, publishedContent = {} } = {}) => ({
  draftContent,
  publishedContent,
  isPublished: trait({ publishedContent: { ...draftContent, ...publishedContent } }),
});

export const withVersionedLanguages = ({ draft, published = {}, defaultLanguage = 'en' } = {}) =>
  Object.keys(draft).reduce(
    (acc, language) => {
      const publishedTraitContent = {
        [language]: {
          draftContent: draft[language],
          publishedContent: published[language] || draft[language],
        },
      };

      if (language === defaultLanguage) {
        acc[language].draftContent = draft[language];
        acc.isPublished = trait(publishedTraitContent);
      } else {
        acc[`isDraft:${language}`] = trait({
          [language]: { draftContent: draft[language] },
        });
        acc[`isPublished:${language}`] = trait(publishedTraitContent);
      }

      return acc;
    },
    { [defaultLanguage]: {} },
  );

export const combineTraits = (...args) => {
  const traits = args.reduce((acc, arg) => {
    Object.entries(arg).forEach(([key, value]) => {
      if (typeof value !== 'object') return;
      if (!value?.__isTrait__) return;

      acc.push([key, value]);
    });

    return acc;
  }, []);

  return traits.reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: {
        __isTrait__: true,
        extension: {
          ...acc[key]?.extension,
          ...value?.extension,
        },
      },
    }),
    {},
  );
};

export const combine = (...args) => ({
  ...args.reduce((acc, arg) => ({ ...acc, ...arg }), {}),
  ...combineTraits(...args),
});

export const VersionedFactory = {
  extend: ({ dateCreated, dateModified, datePublished, draft, published, defaultLanguage, ...other }) =>
    Factory.extend({
      ...combine(
        withVersionDate({
          dateCreated,
          dateModified,
          datePublished,
          languages: Object.keys(draft),
        }),
        withVersionedLanguages({
          draft,
          published,
          defaultLanguage,
        }),
      ),
      ...other,
    }),
};
